/**
 * DurationInput Component
 *
 * A modal-based duration input that provides a user-friendly time picker.
 * Displays time in readable format and opens a modal with +/- controls when clicked.
 *
 * Features:
 * - Automatically receives duration from timers (when timer is stopped)
 * - Click-to-edit modal interface for manual adjustments
 * - Large touch-friendly +/- buttons
 * - Separate hour and minute controls
 * - Direct number input support
 * - No cursor jumping issues
 * - Stores value as minutes string for backend compatibility
 */

import { useState } from 'react';
import TimePickerModal from './TimePickerModal';

interface DurationInputProps {
  value: string; // minutes as string (e.g., "83")
  onChange: (minutes: string) => void;
  label: string;
  placeholder?: string;
  className?: string;
}

/**
 * Format minutes as human-readable string
 * @param minutes - Total minutes as number
 * @returns Formatted string like "1h 30m" or "0h 00m"
 */
function formatDuration(minutes: number): string {
  if (isNaN(minutes) || minutes < 0) return '0h 00m';

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return `${hours}h ${mins.toString().padStart(2, '0')}m`;
}

export default function DurationInput({
  value,
  onChange,
  label,
  placeholder = '0h 00m',
  className,
}: DurationInputProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const minutes = parseInt(value, 10) || 0;
  const displayValue = value ? formatDuration(minutes) : placeholder;

  const handleSave = (totalMinutes: number) => {
    onChange(totalMinutes.toString());
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className={
          className ||
          'w-full px-4 py-2 text-left border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors'
        }
        aria-label={`${label}: ${displayValue}`}
      >
        <span className={value ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}>
          {displayValue}
        </span>
      </button>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Click to set or adjust duration
      </p>

      <TimePickerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialMinutes={minutes}
        onSave={handleSave}
        title={label}
      />
    </div>
  );
}
